import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiPhone, FiMapPin, FiCalendar, FiCheckCircle } = FiIcons;

const Contact = () => {
  const contactMethods = [
    {
      icon: FiMail,
      title: "Email",
      value: "hello@wethebillions.com",
      link: "mailto:hello@wethebillions.com",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: FiPhone,
      title: "Call/Text",
      value: "(202) 656-0054",
      link: "tel:+12026560054",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: FiMapPin,
      title: "Service Areas",
      value: "Virtual Sessions (Globally) & In-Person Sessions (MD & VA)",
      link: null,
      color: "from-purple-500 to-pink-500"
    }
  ];

  const consultationBenefits = [
    "Confidential assessment of your recovery challenges",
    "Personalized EF therapy recommendations",
    "Clear understanding of how we can help",
    "No pressure, just information and support"
  ];

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Ready to Build a{' '}
            <span className="bg-gradient-to-r from-accent-magenta to-accent-purple bg-clip-text text-transparent">
              Resilient Brain?
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Take the first step toward sustainable recovery. Book your free consultation today.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-8">
              Get in Touch
            </h3>

            <div className="space-y-6 mb-8">
              {contactMethods.map((method, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className={`w-12 h-12 bg-gradient-to-r ${method.color} rounded-xl flex items-center justify-center`}>
                    <SafeIcon icon={method.icon} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{method.title}</h4>
                    {method.link ? (
                      <a
                        href={method.link}
                        className="text-accent-magenta hover:text-accent-magenta/80 transition-colors duration-200"
                      >
                        {method.value}
                      </a>
                    ) : (
                      <p className="text-gray-600">{method.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
              className="bg-accent-light/30 rounded-2xl p-6"
            >
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 text-accent-magenta" />
                What to Expect in Your Free Consultation:
              </h4>
              <div className="space-y-3">
                {consultationBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-accent-magenta flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Booking Widget */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-8">
              Book Your Free Consultation
            </h3>

            <div className="bg-gray-50 rounded-3xl p-2">
              <iframe
                allowFullScreen
                height="717"
                width="100%"
                frameBorder="0"
                title="Book Your Consultation - We The Billions"
                src="https://wethebillions.neetocal.com/embed/5f6e78fe-d1ce-43f6-b1ce-797e0cec501a"
                className="rounded-2xl"
              />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="mt-6 text-center"
            >
              <p className="text-gray-600 mb-4">
                Having trouble with the booking widget?
              </p>
              <a
                href="https://wethebillions.neetocal.com/consultation"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-magenta hover:text-accent-magenta/80 font-semibold underline"
              >
                Click here to book directly
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Website Link */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-gray-600">
            Visit our website:{' '}
            <a
              href="https://www.wethebillions.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-magenta hover:text-accent-magenta/80 font-semibold"
            >
              www.wethebillions.com
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;